import React, { useState, useEffect, useCallback } from 'react';
import { Spin } from 'antd';
import { Form, Input, Select, DatePicker, InputNumber, Upload, Button, Card, Row, Col, message } from 'antd';
import zhCN from 'antd/es/date-picker/locale/zh_CN';
import { UploadOutlined, UserOutlined, SearchOutlined, PhoneOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import api from '../../api/http';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

// 请假类型选项 - 按类别分组
const LEAVE_TYPES = [
  { label: '事假', value: '事假', category: '普通请假' },
  { label: '调休', value: '调休', category: '普通请假' },
  { label: '年假', value: '年假', category: '法定休假' },
  { label: '婚假', value: '婚假', category: '法定休假' },
  { label: '丧假', value: '丧假', category: '法定休假' },
  { label: '护产假', value: '护产假', category: '特殊情况' },
  { label: '产假', value: '产假', category: '特殊情况' },
  { label: '病假', value: '病假', category: '健康相关' },
  { label: '工伤假', value: '工伤假', category: '健康相关' },
  { label: '其他', value: '其他', category: '其他' }
];

// 按类别分组请假类型
const groupLeaveTypes = (types) => {
  const grouped = {};
  types.forEach(type => {
    if (!grouped[type.category]) {
      grouped[type.category] = [];
    }
    grouped[type.category].push(type);
  });
  return grouped;
};

const NewLeaveApplicationForm = ({ onSubmitted, onClose } = {}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeesError, setEmployeesError] = useState('');
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentsError, setAgentsError] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  // 初始化数据
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // 获取部门列表
        const deptResponse = await api.get('/departments');
        if (deptResponse && deptResponse.data) {
          setDepartments(deptResponse.data.map(dept => ({ label: dept.name, value: dept.id })));
        }

        // 获取员工列表
        setEmployeesLoading(true);
        setEmployeesError(null);
        const empResponse = await api.get('/employees');
        if (empResponse && empResponse.data) {
          // 确保数据格式正确
          const formattedEmployees = empResponse.data
            .filter(emp => emp && emp.name) // 过滤掉无效数据
            .map(emp => ({ 
              label: emp.name, 
              value: emp.id || emp.employee_id || emp.id_card_number || Math.random(), // 确保有唯一值
              name: emp.name,
              department: emp.department || emp.department_id || emp.dept_id || emp.dept
            }));
          setEmployees(formattedEmployees);
        }

              // 预填申请人信息
        if (user) {
          // 尝试从用户对象中提取必要信息，支持多种可能的字段名称
          const userId = user.employee_id || user.employeeID || user.id || user.user_id;
          const userName = user.name || user.username || user.fullName || user.real_name;
          const department = user.department || user.department_id || user.dept_id || user.dept;
          
          // 只有当至少有用户名时才进行预填
          if (userName) {
            const prefillData = {
              employee_name: userName,
              employee_id: userId || '' // 确保employee_id始终被设置
            };
            
            
            // 只有当有部门信息时才设置
            if (department) {
              prefillData.departments = Array.isArray(department) ? department : [department];
            }
            
            form.setFieldsValue(prefillData);
            
            // 同时也尝试自动填充到员工搜索字段
            if (userName) {
              setSearchValue(userName);
              // 立即过滤员工列表
              const searchLower = userName.toLowerCase();
              const filtered = employees.filter(emp => {
                if (!emp || !emp.label) return false;
                return emp.label.toLowerCase().includes(searchLower);
              });
              setFilteredEmployees(filtered);
            }
          }
        } else {
          console.warn('用户信息未加载，无法预填表单');
        }
      } catch (error) {
        console.error('获取初始化数据失败:', error);
        if (error.response && error.response.config && error.response.config.url === '/employees') {
          setEmployeesError('获取员工列表失败: ' + (error.message || '未知错误'));
          console.error('员工API错误详情:', error.response?.status, error.response?.data);
          // 提供一些模拟数据以便测试
          const mockEmployees = [
            { label: '张三', value: '1' },
            { label: '李四', value: '2' },
            { label: '王五', value: '3' },
            { label: '赵六', value: '4' },
            { label: '钱七', value: '5' }
          ];
          setEmployees(mockEmployees);
          message.warning('员工数据加载失败，已使用模拟数据');
        } else if (error.response && error.response.config && error.response.config.url === '/departments') {
          message.error('获取部门数据失败');
        }
      } finally {
        setEmployeesLoading(false);
      }
    };

    fetchInitialData();
  }, [user, form]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleMediaChange = (event) => {
      setIsMobile(event.matches);
    };
    setIsMobile(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleMediaChange);
      return () => mediaQuery.removeEventListener('change', handleMediaChange);
    }
    mediaQuery.addListener(handleMediaChange);
    return () => mediaQuery.removeListener(handleMediaChange);
  }, []);

  // 处理员工搜索
  const handleEmployeeSearch = useCallback(async (value) => {
    if (!value.trim()) {
      setFilteredEmployees([]);
      return;
    }
    
    setEmployeesLoading(true);
    try {
      const response = await api.get(`/employees?name=${encodeURIComponent(value.trim())}`);
      if (response && response.data && Array.isArray(response.data)) {
        const filtered = response.data.map(emp => ({
          value: emp.id,
          label: `${emp.name} - ${emp.department || ''}${emp.section ? '-' + emp.section : ''}`,
          name: emp.name,
          department: emp.department,
          section: emp.section
        }));
        setFilteredEmployees(filtered);
      } else {
        setFilteredEmployees([]);
      }
    } catch (error) {
      console.error('搜索员工失败:', error);
      setEmployeesError('搜索员工失败，请稍后重试');
      setFilteredEmployees([]);
    } finally {
      setEmployeesLoading(false);
    }
  }, []);

  // 处理工作代理人搜索
  const handleAgentSearch = useCallback(async (value) => {
    if (!value.trim()) {
      setFilteredAgents([]);
      return;
    }
    
    setAgentsLoading(true);
    try {
      const response = await api.get(`/employees?name=${encodeURIComponent(value.trim())}`);
      if (response && response.data && Array.isArray(response.data)) {
        const currentEmployeeId = form.getFieldValue('employee_id');
        const filtered = response.data
          .filter(emp => !currentEmployeeId || emp.id !== currentEmployeeId) // 排除当前员工
          .map(emp => ({
            value: emp.id,
            label: `${emp.name} - ${emp.department || ''}${emp.section ? '-' + emp.section : ''}`,
            name: emp.name,
            department: emp.department,
            section: emp.section
          }));
        setFilteredAgents(filtered);
      } else {
        setFilteredAgents([]);
      }
    } catch (error) {
      console.error('搜索工作代理人失败:', error);
      setAgentsError('搜索工作代理人失败，请稍后重试');
      setFilteredAgents([]);
    } finally {
      setAgentsLoading(false);
    }
  }, [form]);

  // 处理员工选择
  const handleEmployeeSelect = (value, option) => {
    if (!option) return;

    const matchedDept = departments.find((dept) => dept.label === option.department || dept.value === option.department);
    const deptValue = matchedDept ? matchedDept.value : (option.department ? option.department : []);
    
    // 设置员工ID和姓名
    form.setFieldsValue({
      employee_id: value,
      employee_name: option.name,
      departments: deptValue ? [deptValue] : []
    });
  };

  const updateLeaveDays = (range) => {
    if (!range || range.length !== 2) {
      form.setFieldsValue({ leave_days: undefined });
      return;
    }
    const [start, end] = range;
    const diffDays = end.diff(start, 'day') + 1;
    form.setFieldsValue({ leave_days: diffDays > 0 ? diffDays : undefined });
  };



  // 文件上传处理
  const handleUploadChange = ({ fileList }) => {
    setUploadedFiles(fileList);
  };

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

  // 自定义日期范围验证
  const validateDateRange = (_, value) => {
    if (!value || value.length !== 2) {
      return Promise.reject('请选择完整的请假日期范围');
    }
    
    const [start, end] = value;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (start.isBefore(today, 'day')) {
      return Promise.reject('开始日期不能早于今天');
    }
    
    if (start.isAfter(end, 'day')) {
      return Promise.reject('开始日期不能晚于结束日期');
    }
    
    const diffDays = end.diff(start, 'day') + 1;
    if (diffDays > 30) {
      return Promise.reject('请假天数不能超过30天');
    }
    
    return Promise.resolve();
  };

  // 验证请假类型选择
  const validateLeaveTypes = (_, value) => {
    if (!value || value.length === 0) {
      return Promise.reject('请至少选择一种请假类型');
    }
    
    if (value.length > 3) {
      return Promise.reject('最多只能选择3种请假类型');
    }
    
    // 检查请假类型组合是否合理
    const hasMaternity = value.includes('产假');
    const hasMarriage = value.includes('婚假');
    
    if (hasMaternity && hasMarriage) {
      return Promise.reject('产假和婚假不能同时选择');
    }
    
    return Promise.resolve();
  };

  // 验证部门选择
  const validateDepartments = (_, value) => {
    if (!value || value.length === 0) {
      return Promise.reject('请至少选择一个部门');
    }
    
    if (value.length > 3) {
      return Promise.reject('最多只能选择3个部门');
    }
    
    return Promise.resolve();
  };

  // 提交表单
  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const attachments = await Promise.all(
        (uploadedFiles || []).map(async (file) => {
          const url = file.url || file.thumbUrl || (file.originFileObj ? await toBase64(file.originFileObj) : '');
          if (!url) return null;
          return { name: file.name, url };
        })
      );

      const dateRange = values.date_range || (values.date_start && values.date_end ? [values.date_start, values.date_end] : []);

      if (!dateRange || dateRange.length !== 2) {
        message.error('请选择完整的请假日期范围');
        setLoading(false);
        return;
      }

      // 准备提交数据
      // 注意：根据后端API要求，使用普通的JSON格式而不是FormData
      const submitData = {
        employee_id: values.employee_id || form.getFieldValue('employee_id') || '',
        // 后端需要单个leave_type而不是leave_types数组，取第一个选中的值
        leave_type: values.leave_types && values.leave_types.length > 0 ? values.leave_types[0] : '',
        start_date: dateRange[0].format('YYYY-MM-DD'),
        end_date: dateRange[1].format('YYYY-MM-DD'),
        // 将数字时间转换为HH:MM格式
        start_time: values.start_time ? `${values.start_time.toString().padStart(2, '0')}:00` : '09:00',
        end_time: values.end_time ? `${values.end_time.toString().padStart(2, '0')}:00` : '18:00',
        reason: values.reason,
        attachments: attachments.filter(Boolean)
      };
      
      // 验证必填字段
      if (!submitData.employee_id) {
        message.error('请选择有效的员工');
        setLoading(false);
        return;
      }


      // 提交请假申请
      const response = await api.post('/leave-overtime/leave', submitData);

      if (response) {
        message.success('请假申请提交成功');
        form.resetFields();
        setUploadedFiles([]);
        if (typeof onSubmitted === 'function') onSubmitted();
        if (typeof onClose === 'function') onClose();
        else navigate('/daily/leave-overtime');
      } else {
        message.error(response?.message || '请假申请提交失败');
      }
    } catch (error) {
      console.error('提交请假申请失败:', error);
      message.error('提交请假申请失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="new-leave-application-form">
      <Card 
        title="员工请假申请系统" 
        className="leave-application-form"
        size="small"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            start_time: 9,
            end_time: 18,
            approval_days: 1,
            leave_days: 1
          }}
          scrollToFirstError={true}
          className="responsive-form"
        >
          {/* 申请人信息 */}
          <Form.Item
            label="申请人信息"
            className="section-title"
          >
            <div className="info-section">
              <div className="info-item">
                <Form.Item
                  name="employee_id"
                  hidden
                  initialValue=""
                />
                <Form.Item
                  name="employee_name"
                  label="申请人姓名"
                  rules={[
                    { required: true, message: '请选择员工名称' },
                    { validator: (_, value) => {
                        // 验证是否已选择了有效的员工
                        if (value && !form.getFieldValue('employee_id')) {
                          return Promise.reject('请从下拉列表中选择有效的员工');
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                  className="auto-complete-wrapper"
                  tooltip="请从下拉列表中选择有效的员工"
                >
                  <Select
                    showSearch
                    placeholder="输入姓名检索"
                    filterOption={false}
                    onSearch={(kw) => handleEmployeeSearch(kw)}
                    onSelect={handleEmployeeSelect}
                    options={filteredEmployees}
                    notFoundContent={employeesLoading ? <Spin size="small" /> : null}
                    style={{ width: '100%' }}
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  />
                  {employeesError && (
                    <div className="error-message" style={{ color: 'red', marginTop: 5 }}>
                      {employeesError}
                    </div>
                  )}
                </Form.Item>
              </div>
              
              <div className="info-item">
                <Form.Item
                  name="departments"
                  label="部门"
                  rules={[
                    { required: true, message: '请至少选择一个部门' },
                    { validator: validateDepartments }
                  ]}
                  tooltip="请选择员工所属部门，最多选择3个"
                >
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children.toLowerCase().includes(input.toLowerCase())
                    }
                    tokenSeparators={[',', ' ']}
                    maxTagCount="responsive"
                    placeholder="请选择或搜索部门"
                  >
                    {departments.length > 0 ? (
                      departments
                        .sort((a, b) => a.label.localeCompare(b.label)) // 按名称排序
                        .map(dept => (
                          <Option key={dept.value} value={dept.value}>
                            {dept.label}
                          </Option>
                        ))
                    ) : (
                      <Option value="" disabled>
                        加载部门中...
                      </Option>
                    )}
                  </Select>
                </Form.Item>
              </div>
              
              <div className="info-item">
                <Form.Item
                  name="emergency_contact"
                  label="紧急联系人"
                  rules={[{ required: false }]}
                >
                  <Input prefix={<PhoneOutlined />} placeholder="请输入紧急联系人" />
                </Form.Item>
              </div>
            </div>
          </Form.Item>

          {/* 请假信息 */}
          <Form.Item
            label="请假信息"
            className="section-title"
          >
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                    name="leave_types"
                    label="请假类型"
                    rules={[
                      { required: true, message: '请至少选择一种请假类型' },
                      { validator: validateLeaveTypes }
                    ]}
                    tooltip="可选择多种请假类型，最多选择3种"
                  >
                    <Select
                      mode="multiple"
                      placeholder="请选择请假类型"
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) => {
                        // 搜索选项文本和所属组
                        const searchText = input.toLowerCase();
                        return option.children.toLowerCase().includes(searchText) ||
                               (option.props && option.props.dataCategory && 
                                option.props.dataCategory.toLowerCase().includes(searchText));
                      }}
                      maxTagCount="responsive"
                      tokenSeparators={[',', ' ']}
                      size="middle"
                    >
                      {/* 按类别分组显示请假类型 */}
                      {Object.entries(groupLeaveTypes(LEAVE_TYPES)).map(([category, types]) => (
                        <Select.OptGroup key={category} label={category}>
                          {types.map(type => (
                            <Option 
                              key={type.value} 
                              value={type.value}
                              dataCategory={category}
                            >
                              {type.label}
                            </Option>
                          ))}
                        </Select.OptGroup>
                      ))}
                    </Select>
                  </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="work_agent"
                  label="工作代理人"
                  rules={[{ required: true, message: '请选择工作代理人' }]}
                  tooltip="请选择在您请假期间代理工作的同事"
                  className="auto-complete-wrapper"
                >
                  <Select
                    showSearch
                    placeholder="输入姓名、部门或科室检索"
                    filterOption={false}
                    onSearch={(kw) => handleAgentSearch(kw)}
                    onSelect={(value, option) => {
                      form.setFieldsValue({
                        work_agent: value
                      });
                    }}
                    options={filteredAgents}
                    notFoundContent={agentsLoading ? <Spin size="small" /> : null}
                    style={{ width: '100%' }}
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                    size="middle"
                  />
                  {agentsError && (
                    <div className="error-message" style={{ color: 'red', marginTop: 5 }}>
                      {agentsError}
                    </div>
                  )}
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="approval_days"
                  label="审批天数"
                  rules={[
                    { required: true, message: '请输入审批天数' },
                    { type: 'number', min: 1, message: '审批天数至少为1天' },
                    { type: 'number', max: 30, message: '审批天数最多为30天' },
                    { validator: (_, value) => {
                        // 验证审批天数合理性
                        if (value > 10) {
                          return Promise.reject('审批天数超过10天，请确认是否合理');
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                  tooltip="请根据公司规定设置合理的审批天数"
                >
                  <InputNumber
                    min={1}
                    max={30}
                    placeholder="审批天数"
                    addonAfter="天"
                    size="middle"
                    style={{ width: '100%' }}
                    formatter={(value) => `${value} 天`}
                    parser={(value) => parseInt(value) || 0}
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  label="请假日期范围"
                  tooltip="请假开始日期不能早于今天，且请假天数不能超过30天"
                >
                  {isMobile ? (
                    <Row gutter={8}>
                      <Col xs={12}>
                        <Form.Item
                          name="date_start"
                          rules={[{ required: true, message: '请选择开始日期' }]}
                        >
                          <DatePicker
                            locale={zhCN}
                            style={{ width: '100%' }}
                            placeholder="开始日期"
                            onChange={(value) => {
                              const endDate = form.getFieldValue('date_end');
                              const range = value && endDate ? [value, endDate] : [];
                              form.setFieldsValue({ date_range: range });
                              updateLeaveDays(range);
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={12}>
                        <Form.Item
                          name="date_end"
                          rules={[{ required: true, message: '请选择结束日期' }]}
                        >
                          <DatePicker
                            locale={zhCN}
                            style={{ width: '100%' }}
                            placeholder="结束日期"
                            onChange={(value) => {
                              const startDate = form.getFieldValue('date_start');
                              const range = startDate && value ? [startDate, value] : [];
                              form.setFieldsValue({ date_range: range });
                              updateLeaveDays(range);
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  ) : (
                    <Form.Item
                      name="date_range"
                      rules={[
                        { required: true, message: '请选择完整的请假日期范围' },
                        { validator: validateDateRange }
                      ]}
                    >
                      <RangePicker
                        locale={zhCN}
                        style={{ width: '100%' }}
                        placeholder={['开始日期', '结束日期']}
                        onChange={(range) => updateLeaveDays(range)}
                      />
                    </Form.Item>
                  )}
                </Form.Item>
                {isMobile && (
                  <Form.Item
                    name="date_range"
                    rules={[
                      { required: true, message: '请选择完整的请假日期范围' },
                      { validator: validateDateRange }
                    ]}
                    hidden
                  >
                    <Input type="hidden" />
                  </Form.Item>
                )}
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="leave_days"
                  label="请假天数"
                >
                  <InputNumber
                    min={1}
                    placeholder="自动计算"
                    addonAfter="天"
                    disabled
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="start_time"
                  label="每日开始时间"
                  rules={[
                    { required: true, message: '请输入每日开始时间' },
                    { type: 'number', min: 0, max: 24, message: '开始时间必须在0-24之间' },
                    { validator: (_, value) => {
                        const endTime = form.getFieldValue('end_time');
                        if (value && endTime && value >= endTime) {
                          return Promise.reject('开始时间必须早于结束时间');
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <InputNumber
                    min={0}
                    max={24}
                    placeholder="开始时间"
                    addonAfter="点"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="end_time"
                  label="每日结束时间"
                  rules={[
                    { required: true, message: '请输入每日结束时间' },
                    { type: 'number', min: 0, max: 24, message: '结束时间必须在0-24之间' },
                    { validator: (_, value) => {
                        const startTime = form.getFieldValue('start_time');
                        if (value && startTime && value <= startTime) {
                          return Promise.reject('结束时间必须晚于开始时间');
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <InputNumber
                    min={0}
                    max={24}
                    placeholder="结束时间"
                    addonAfter="点"
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name="reason"
                  label="请假原因"
                  rules={[
                    { required: true, message: '请输入请假原因' },
                    { min: 10, message: '请假原因至少10个字符' },
                    { max: 500, message: '请假原因最多500个字符' },
                    { pattern: /[^<>]/, message: '请假原因不能包含特殊字符' }
                  ]}
                  tooltip="请详细描述请假原因，便于审批人了解情况"
                >
                  <TextArea rows={4} placeholder="请详细说明请假原因" />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>

          {/* 附件上传 */}
          <Form.Item
            label="附件"
            className="section-title"
          >
            <Upload
              multiple
              fileList={uploadedFiles}
              onChange={handleUploadChange}
              beforeUpload={() => false}
              listType="picture-card"
            >
              <div>
                <UploadOutlined />
                <div className="ant-upload-text">上传附件</div>
              </div>
            </Upload>
          </Form.Item>

          {/* 提交按钮 */}
          <Form.Item>
            <Row justify="end">
              <Button 
                type="default" 
                style={{ marginRight: 16 }}
                onClick={() => form.resetFields()}
              >
                重置
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                disabled={loading}
              >
                提交申请
              </Button>
            </Row>
          </Form.Item>
        </Form>
      </Card>

      <style jsx>{`
        .employee-search {
        position: relative;
      }
      .employee-dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: white;
        border: 1px solid #d9d9d9;
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        max-height: 200px;
        overflow-y: auto;
        z-index: 1000;
        max-width: calc(100vw - 40px);
      }
      .employee-item {
        padding: 8px 12px;
        cursor: pointer;
        transition: background-color 0.2s;
      }
      .employee-item:hover {
        background-color: #f5f5f5;
      }
      .no-result {
        padding: 8px 12px;
        color: #999;
        text-align: center;
      }
      .section-title {
        font-weight: bold;
        margin-bottom: 16px;
        color: #262626;
        border-left: 4px solid #1890ff;
        padding-left: 12px;
        font-size: 16px;
      }
      .new-leave-application-form {
        padding: 16px;
        background: #fafafa;
        min-height: calc(100vh - 100px);
        max-width: 100%;
        box-sizing: border-box;
      }
      .leave-application-form {
        background: white;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        width: 100%;
      }
      .responsive-form {
        width: 100%;
      }
      
      /* 信息区块统一布局样式 */
      .info-section {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        margin-bottom: 8px;
      }
      .info-item {
        flex: 1;
        min-width: calc(33.333% - 16px);
        display: flex;
        flex-direction: column;
      }
      
      /* 确保表单标签样式一致 */
      .ant-form-item-label > label {
        font-weight: 500;
        color: #262626;
        font-size: 14px;
      }
      
      /* 确保输入框和选择器样式一致 */
      .ant-input,
      .ant-select,
      .ant-picker,
      .ant-input-number {
        border-radius: 4px;
        transition: all 0.3s;
      }
      
      /* 确保tooltip样式一致 */
      .ant-form-item-tooltip {
        font-size: 12px;
      }
      
      /* 响应式设计 */
      @media (max-width: 768px) {
        .new-leave-application-form {
          padding: 8px;
        }
        
        .leave-application-form {
          padding: 0;
          background: transparent;
          box-shadow: none;
          border: none;
        }

        .leave-application-form .ant-card-head {
          display: none;
        }

        .leave-application-form .ant-card-body {
          padding: 0;
        }

        .leave-application-form .ant-card-head-title {
          font-size: 16px;
        }

        .leave-application-form .ant-card-body {
          padding: 12px;
        }

        .responsive-form .ant-form-item {
          margin-bottom: 12px;
        }

        .responsive-form .ant-form-item-label {
          padding-bottom: 4px;
        }

        .responsive-form .ant-form-item-label > label {
          font-size: 13px;
        }

        .info-section {
          gap: 8px;
        }

        .info-item {
          min-width: 100%;
        }

        .section-title {
          border-left: none;
          padding-left: 0;
          margin-bottom: 8px;
        }

        .section-title .ant-form-item-label {
          display: none;
        }
        
        .ant-form-item-label > label {
          font-size: 14px;
          line-height: 1.5;
        }
        
        .ant-input,
        .ant-select,
        .ant-picker,
        .ant-input-number,
        .ant-input-textarea {
          font-size: 14px;
          width: 100%;
          box-sizing: border-box;
        }
        
        .employee-dropdown {
          max-width: calc(100vw - 24px);
        }
        
        .employee-item {
          padding: 12px 16px;
        }
      }
      
      @media (min-width: 769px) and (max-width: 1024px) {
        .leave-application-form {
          max-width: 90%;
          margin: 0 auto;
        }
      }
      
      @media (min-width: 1025px) {
        .leave-application-form {
          max-width: 800px;
          margin: 0 auto;
        }
      }
      
      /* 输入框和选择器的响应式样式 */
      .ant-input,
      .ant-select,
      .ant-picker,
      .ant-input-number,
      .ant-input-textarea {
        width: 100%;
        box-sizing: border-box;
      }
      
      /* 移动端触摸优化 */
      @media (hover: none) and (pointer: coarse) {
        .employee-item {
          padding: 12px 16px;
        }
      }
      `}</style>
    </div>
  );
};

export default NewLeaveApplicationForm;